import React from 'react';
import {
  XMarkIcon,
  BuildingOfficeIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  ClockIcon,
  ChatBubbleBottomCenterTextIcon,
  TagIcon,
  FlagIcon,
  UserCircleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

interface Contact {
  id: number;
  name: string;
  company: string;
  phone: string;
  email: string;
  business_type: string;
  business_type_display: string;
  message?: string;
  status: string;
  status_display: string;
  priority: string;
  priority_display: string;
  assigned_to?: string;
  admin_notes?: string;
  created_at: string;
  updated_at: string;
  first_contact_date?: string;
  last_contact_date?: string;
  next_followup_date?: string;
}

interface ContactDetailModalProps {
  contact: Contact | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (contact: Contact) => void;
}

const ContactDetailModal: React.FC<ContactDetailModalProps> = ({
  contact,
  isOpen,
  onClose,
  onEdit,
}) => {
  if (!isOpen || !contact) return null;

  const getStatusBadgeColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      new: 'bg-blue-100 text-blue-800 border-blue-200',
      reviewing: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      demo_scheduled: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      quote_sent: 'bg-orange-100 text-orange-800 border-orange-200',
      negotiating: 'bg-pink-100 text-pink-800 border-pink-200',
      followup_waiting: 'bg-gray-100 text-gray-800 border-gray-200',
      completed_success: 'bg-green-100 text-green-800 border-green-200',
      completed_rejected: 'bg-red-100 text-red-800 border-red-200',
      completed_postponed: 'bg-gray-100 text-gray-600 border-gray-200',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityBadgeColor = (priority: string) => {
    const priorityColors: { [key: string]: string } = {
      low: 'bg-green-100 text-green-800 border-green-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      urgent: 'bg-red-100 text-red-800 border-red-200',
    };
    return (
      priorityColors[priority] || 'bg-gray-100 text-gray-800 border-gray-200'
    );
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'short',
      }),
      time: date.toLocaleTimeString('ko-KR', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  };

  const createdAt = formatDateTime(contact.created_at);
  const updatedAt = formatDateTime(contact.updated_at);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* 배경 오버레이 */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* 모달 컨테이너 */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
          {/* 헤더 */}
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {contact.company}
                </h2>
                <p className="text-sm text-gray-500">문의 ID: #{contact.id}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => onEdit(contact)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                편집
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* 상태 및 우선순위 */}
          <div className="py-4 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <TagIcon className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">상태:</span>
                <span
                  className={`inline-flex px-3 py-1 text-sm font-medium rounded-full border ${getStatusBadgeColor(contact.status)}`}
                >
                  {contact.status_display}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <FlagIcon className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  우선순위:
                </span>
                <span
                  className={`inline-flex px-3 py-1 text-sm font-medium rounded-full border ${getPriorityBadgeColor(contact.priority)}`}
                >
                  {contact.priority_display}
                </span>
              </div>
            </div>
          </div>

          {/* 메인 콘텐츠 */}
          <div className="py-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* 왼쪽: 고객 정보 */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    고객 정보
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <UserIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          담당자명
                        </div>
                        <div className="text-sm text-gray-900">
                          {contact.name}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          업체명
                        </div>
                        <div className="text-sm text-gray-900">
                          {contact.company}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <PhoneIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          연락처
                        </div>
                        <div className="text-sm text-gray-900">
                          <a
                            href={`tel:${contact.phone}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {contact.phone}
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          이메일
                        </div>
                        <div className="text-sm text-gray-900">
                          <a
                            href={`mailto:${contact.email}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {contact.email}
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <TagIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          사업분야
                        </div>
                        <div className="text-sm text-gray-900">
                          {contact.business_type_display}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 관리 정보 */}
                {(contact.assigned_to || contact.admin_notes) && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      관리 정보
                    </h3>
                    <div className="space-y-4">
                      {contact.assigned_to && (
                        <div className="flex items-center space-x-3">
                          <UserCircleIcon className="w-5 h-5 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-700">
                              담당자
                            </div>
                            <div className="text-sm text-gray-900">
                              {contact.assigned_to}
                            </div>
                          </div>
                        </div>
                      )}

                      {contact.admin_notes && (
                        <div className="flex items-start space-x-3">
                          <DocumentTextIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-gray-700">
                              관리자 메모
                            </div>
                            <div className="text-sm text-gray-900 whitespace-pre-wrap">
                              {contact.admin_notes}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* 오른쪽: 문의 내용 및 일정 */}
              <div className="space-y-6">
                {/* 문의 내용 */}
                {contact.message && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      문의 내용
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                          {contact.message}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 일정 정보 */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    일정 정보
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <CalendarIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          접수일시
                        </div>
                        <div className="text-sm text-gray-900">
                          {createdAt.date}
                        </div>
                        <div className="text-sm text-gray-500">
                          {createdAt.time}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <ClockIcon className="w-5 h-5 text-green-500 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          최근 업데이트
                        </div>
                        <div className="text-sm text-gray-900">
                          {updatedAt.date}
                        </div>
                        <div className="text-sm text-gray-500">
                          {updatedAt.time}
                        </div>
                      </div>
                    </div>

                    {contact.first_contact_date && (
                      <div className="flex items-start space-x-3">
                        <CalendarIcon className="w-5 h-5 text-purple-500 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            첫 상담일
                          </div>
                          <div className="text-sm text-gray-900">
                            {formatDateTime(contact.first_contact_date).date}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDateTime(contact.first_contact_date).time}
                          </div>
                        </div>
                      </div>
                    )}

                    {contact.last_contact_date && (
                      <div className="flex items-start space-x-3">
                        <CalendarIcon className="w-5 h-5 text-orange-500 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            마지막 상담일
                          </div>
                          <div className="text-sm text-gray-900">
                            {formatDateTime(contact.last_contact_date).date}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDateTime(contact.last_contact_date).time}
                          </div>
                        </div>
                      </div>
                    )}

                    {contact.next_followup_date && (
                      <div className="flex items-start space-x-3">
                        <CalendarIcon className="w-5 h-5 text-red-500 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            다음 팔로업 예정일
                          </div>
                          <div className="text-sm text-gray-900">
                            {formatDateTime(contact.next_followup_date).date}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDateTime(contact.next_followup_date).time}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 푸터 */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              닫기
            </button>
            <button
              onClick={() => onEdit(contact)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              편집하기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailModal;
